import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from '@emotion/styled'
import useSponsor from '@Hooks/useSponsor'
import { displayErrorMessage } from '@Lib/Util'
import { Button, Drawer, message } from 'antd'

import useLeadScanQuestions from '../Hooks/useLeadScanQuestions'
import { LeadScanQuestion } from '../interfaces'
import { FIELD_TYPES } from '../LeadScanQuestions'

import LeadScanQuestionForm from './LeadScanQuestionForm'


type FormProps = {
  fieldType: LeadScanQuestion['fieldType'],
  title: string,
  options: string[]
}

interface LeadScanQuestionDrawerProps {
  visible: boolean,
  leadScanQuestion: LeadScanQuestion | null,
  onClose: () => void
}

export default function LeadScanQuestionDrawer({ visible, leadScanQuestion, onClose }: LeadScanQuestionDrawerProps) {
  const { sponsor: { id: sponsorId } } = useSponsor()
  const isEditMode = Boolean(leadScanQuestion)
  const form = useForm<FormProps>({
    mode: 'onChange',
    defaultValues: {
      fieldType: leadScanQuestion?.fieldType || 'Text',
      title: leadScanQuestion?.title || '',
      options: leadScanQuestion?.options || []
    }
  })

  const { create, update } = useLeadScanQuestions({ sponsorId })

  const handleSubmit = form.handleSubmit(({ title, fieldType, options }) => {
    const action = isEditMode ? update : create
    const hasOptions = FIELD_TYPES[fieldType]?.hasOptions

    action.mutate({
      id: leadScanQuestion?.id,
      data: {
        question: {
          field_type: fieldType,
          title: title,
          options: hasOptions ? options : undefined
        }
      }
    }, {
      onSuccess: () => {
        message.success(`Lead scan question ${isEditMode ? 'updated' : 'created'}`)
        onClose()
        form.reset()
      },
      onError: displayErrorMessage
    })
  })

  return (
    <FormProvider {...form}>
      <Drawer
        title={'Lead scan question'}
        open={visible}
        onClose={onClose}
        footer={(
          <DrawerFooter>
            <Button onClick={onClose}>
              {'Cancel'}
            </Button>
            <Button
              type={'primary'}
              onClick={handleSubmit}
              loading={create.isLoading || update.isLoading}
            >
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          </DrawerFooter>
        )}
      >
        <LeadScanQuestionForm />
      </Drawer>
    </FormProvider>
  )
}

const DrawerFooter = styled.div(({ theme }) => `
  display: flex;
  justify-content: flex-end;
  gap: ${theme.margins.halfBaseMargin}px;
`)
